/**
 * The wallet log's header behaviour, for the pages that scroll the window.
 *
 * Asked for as "the same scroll mimic as the wallet log on other pages: a
 * small header stays on the top, a click or tap on it brings the whole
 * header, scrolling down leaves the small header".
 *
 * The log does it by folding: its list scrolls in a box of its own, so the
 * header above can shrink without moving what is being read. Every other
 * page scrolls the window, and there folding would move the page twice —
 * once for the scroll and once for the header giving up its height. So the
 * same three things are got another way, with nothing ever changing height:
 *
 *  - The page's own bar (its title and main buttons, or its filters) is
 *    sticky, so it is what stays at the top once the nav has scrolled away —
 *    the small header.
 *  - A click on the bar's empty space brings the whole header back by
 *    pinning the header's parts (the nav, and the totals where a page has
 *    them) at the top too, above the bar. Pinning a sticky element does not
 *    change the flow, so the page underneath does not move: the header comes
 *    down over it, and the bar moves down beneath it.
 *  - The next downward scroll lets them go again, as on the log.
 *
 * `--wl-head-h` on <html> is the height of what has been brought back, so
 * the bar — and anything else a page keeps sticky — sits under it rather
 * than behind it.
 */

export interface HeadReveal {
  /** Let the header go, as a downward scroll does. */
  reset(): void;
}

let active: { bar: HTMLElement; parts: () => HTMLElement[]; shown: boolean } | undefined;
let lastY = 0;
let listening = false;

const PART = 'wl-head-part--shown';

function parts(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>('.wl-head-fold')];
}

function hide(): void {
  if (!active?.shown) return;
  active.shown = false;
  for (const part of active.parts()) {
    part.classList.remove(PART);
    part.style.removeProperty('top');
  }
  document.documentElement.style.removeProperty('--wl-head-h');
}

function show(): void {
  if (!active) return;
  const list = active.parts().filter((p) => p.offsetHeight > 0);
  // Already on screen where it is: at the top of the page there is nothing
  // to bring back, and pinning it would only stop it scrolling away.
  const first = list[0];
  if (!first || first.getBoundingClientRect().top >= 0) return;
  let offset = 0;
  for (const part of list) {
    part.style.top = `${offset}px`;
    part.classList.add(PART);
    offset += part.offsetHeight;
  }
  document.documentElement.style.setProperty('--wl-head-h', `${offset}px`);
  active.shown = true;
  lastY = window.scrollY;
}

/**
 * Make `bar` the page's small header. One page, one bar: wiring a second
 * replaces the first, so nothing is left listening for a page that has gone.
 */
export function wireHeadReveal(bar: HTMLElement, opts: {
  parts?: () => HTMLElement[];
  /** The bar sits on the page rather than on a card, and takes the page's colour. */
  onPage?: boolean;
  /** The bar is sticky and dressed already (the reports' filters): only listen. */
  styled?: boolean;
} = {}): HeadReveal {
  hide();
  active = { bar, parts: opts.parts ?? parts, shown: false };
  if (!opts.styled) bar.classList.add('wl-page-bar');
  if (opts.onPage) bar.classList.add('wl-page-bar--page');
  bar.addEventListener('click', (event) => {
    if (active?.bar !== bar) return;
    // The bar's own space, not what is on it: its buttons and fields keep
    // doing what they do.
    if ((event.target as HTMLElement).closest('button, select, input, textarea, a, label, [role="button"]')) return;
    show();
  });
  if (!listening) {
    listening = true;
    lastY = window.scrollY;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      if (active?.shown && y > lastY + 1) hide();
      lastY = y;
    }, { passive: true });
  }
  return { reset: hide };
}
